import {dynamicCohortEnrollmentsApi} from '@/api/dynamic-cohort-enrollments.api.js'
import {enrollmentJobStatuses} from '@/constants/enrollments.js'
import {useFlashMessages} from '@/composables/use-flash-messages.js'
import {useErrorHandling} from '@/composables/use-error-handling.js'
import {useDelay} from '@/composables/use-delay.js'
import {ref, computed} from 'vue'

/**
 * Composable to manage dynamic cohort enrollmnt
 */
export function useDynamicCohortEnrollment() {

  //Composables
  const {setFlashFailure} = useFlashMessages()
  const {processError} = useErrorHandling()
  const {delay} = useDelay(3000)

  //Refs
  const enrollmentStatus = ref(null)
  const isPolling = ref(false)
  const isErrorStatus = computed(() => [
    enrollmentJobStatuses.FAILED,
  ].includes(enrollmentStatus.value))
  const isTerminalStatus = computed(() => [
    enrollmentJobStatuses.COMPLETED,
    enrollmentJobStatuses.FAILED,
    enrollmentJobStatuses.CANCELLED,
  ].includes(enrollmentStatus.value))

  //Kick off enrollments
  const kickOffEnrollment = async () => {
    try {
      const status = await dynamicCohortEnrollmentsApi.kickOff()
      enrollmentStatus.value = status
      return status
    }
    catch (error) {
      setFlashFailure('Failed to start enrollment. Please try again.')
      processError(error)
      throw error
    }
  }

  //Poll enrollment status
  const pollEnrollmentStatus = async (attempt = 1) => {

    //Fail if max attempts reached
    if (attempt > 20) {
      throw new Error('Enrollment is taking longer than expected. Please try again.')
    }

    //Check status
    const status = await dynamicCohortEnrollmentsApi.checkStatus()
    enrollmentStatus.value = status

    //Throw if failed
    if (status === enrollmentJobStatuses.FAILED) {
      throw new Error('Enrollment failed. Please try again.')
    }

    //Done if terminal status reached
    if (isTerminalStatus.value) {
      return status
    }

    //Delay and recurse
    await delay()
    return pollEnrollmentStatus(attempt + 1)
  }

  //Start polling enrollment status
  const startPolling = async () => {

    //Already in terminal status
    if (isTerminalStatus.value) {
      return
    }

    //Start polling
    try {
      isPolling.value = true
      return await pollEnrollmentStatus()
    }
    catch (error) {
      setFlashFailure(error.message)
      processError(error)
      throw error
    }
    finally {
      isPolling.value = false
    }
  }

  //Stop polling enrollment status (sets status to cancelled, which will stop the polling cycle)
  const stopPolling = () => {
    enrollmentStatus.value = enrollmentJobStatuses.CANCELLED
  }

  //Expose interface
  return {
    kickOffEnrollment,
    startPolling,
    stopPolling,
    isPolling,
    enrollmentStatus,
    isErrorStatus,
    isTerminalStatus,
  }
}
